// Dr.Rx™ UID-Scoped Local Data Management Modal
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;

interface DataManagementModalProps {
    onClose: () => void;
}

const DataManagementModal: React.FC<DataManagementModalProps> = ({ onClose }) => {
    const { uid } = useAuth();
    const { showToast } = useToast();
    const [showConfirm, setShowConfirm] = useState(false);

    const handleClearChats = () => {
        if (uid) {
            localStorage.removeItem(`chatSessions_${uid}`);
            localStorage.removeItem(`activeSessionId_${uid}`);
        }
        localStorage.removeItem('chatSessions');
        showToast('Consultation history cleared on this device.', 'success');
    };

    const handleClearStudy = () => {
        if (uid) {
            localStorage.removeItem(`drrx_missed_cards_${uid}`);
            localStorage.removeItem(`drrx_bookmarked_cards_${uid}`);
            localStorage.removeItem(`drugFavorites_${uid}`);
        }
        localStorage.removeItem('drrx_missed_cards');
        localStorage.removeItem('drrx_bookmarked_cards');
        localStorage.removeItem('drugFavorites');
        showToast('Study progress & drug favorites cleared.', 'success');
    };

    const handleResetSettings = () => {
        if (uid) {
            localStorage.removeItem(`chatSettings_${uid}`);
        }
        localStorage.removeItem('chatSettings');
        showToast('Clinical Co-Pilot settings reset to defaults.', 'info');
    };

    const handleClearAll = () => {
        localStorage.clear();
        setShowConfirm(false);
        showToast('All local app data cleared. Reloading...', 'warning');
        setTimeout(() => window.location.reload(), 900);
    };

    return (
        <div onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50 page-enter-active">
            <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] rounded-2xl border border-blue-900/60 w-full max-w-lg text-gray-300 p-8 shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white"><CloseIcon /></button>
                <h2 className="text-2xl font-bold text-white mb-2">Manage Local App Data</h2>
                <p className="text-gray-400 text-sm mb-6">Manage data cached on this device. These actions cannot be undone.</p>
                
                <div className="space-y-3">
                    <button onClick={handleClearChats} className="w-full text-left p-4 bg-[#101D35] hover:bg-blue-900/40 border border-blue-900/50 rounded-xl font-semibold text-white transition-colors">Clear Consultation History</button>
                    <button onClick={handleClearStudy} className="w-full text-left p-4 bg-[#101D35] hover:bg-blue-900/40 border border-blue-900/50 rounded-xl font-semibold text-white transition-colors">Clear Study Progress & Drug Favorites</button>
                    <button onClick={handleResetSettings} className="w-full text-left p-4 bg-[#101D35] hover:bg-blue-900/40 border border-blue-900/50 rounded-xl font-semibold text-white transition-colors">Reset Co-Pilot Settings</button>
                </div>

                <hr className="border-blue-800/50 my-6" />

                {!showConfirm ? (
                    <button onClick={() => setShowConfirm(true)} className="w-full flex items-center justify-center p-4 bg-red-900/50 hover:bg-red-800/70 border border-red-700/50 rounded-xl text-red-200 font-bold transition-colors">
                        <TrashIcon /> Clear All Local App Data
                    </button>
                ) : (
                    <div className="bg-red-950/60 border border-red-500/50 p-4 rounded-xl text-center">
                        <p className="font-bold text-red-200">Are you sure?</p>
                        <p className="text-xs text-red-300/80 mt-1">This will delete all local consultations, study progress, and settings. The app will reload.</p>
                        <div className="flex gap-4 mt-4 justify-center">
                            <button onClick={() => setShowConfirm(false)} className="px-6 py-2 rounded-xl text-gray-300 font-semibold hover:bg-white/10 transition-colors">Cancel</button>
                            <button onClick={handleClearAll} className="bg-red-600 text-white font-bold py-2 px-6 rounded-xl hover:bg-red-500">Confirm Purge</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataManagementModal;